import React, { useState, useEffect } from 'react';
import QuestionCard from './QuestionCard';
import BuildDescriptionCard from './BuildDescriptionCard';
import CulinaryTermsCard from './CulinaryTermsCard';
import WinePairingCard from './WinePairingCard';
import NutritionalInfoCard from './NutritionalInfoCard';
import FinalScore from './FinalScore';
import QuizReview from './QuizReview';
import './Quiz.css';

const EnhancedQuiz = ({ quizType, onBackToMenu }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [culinaryTerms, setCulinaryTerms] = useState([]);
  const [winePairings, setWinePairings] = useState([]);
  const [nutritionalInfo, setNutritionalInfo] = useState([]);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quizResults, setQuizResults] = useState([]);
  const [showReview, setShowReview] = useState(false);

  // Shuffle array function (Fisher-Yates algorithm)
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Filter menu items based on quiz type
  const filterMenuItems = (items, type) => {
    switch (type) {
      case 'drinks-only':
        return items.filter(item => 
          item.category.includes('Cocktail')
        );
      case 'brunch-only':
        return items.filter(item => 
          item.category.includes('Brunch') || 
          item.category.includes('Appetizer')
        );
      case 'dinner-only':
        return items.filter(item => 
          item.category.includes('Dinner') || 
          item.category.includes('Salad')
        );
      default:
        return items;
    }
  };

  // Load data based on quiz type
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Load basic menu data for most quiz types
        if (!['culinary-terms', 'wine-pairing', 'nutritional-info'].includes(quizType)) {
          const menuResponse = await fetch('/menu.json');
          if (!menuResponse.ok) {
            throw new Error('Failed to load menu data');
          }
          const menuData = await menuResponse.json();
          setMenuItems(menuData);
          
          const filteredItems = filterMenuItems(menuData, quizType);
          setShuffledQuestions(shuffleArray(filteredItems));
        }
        
        // Load culinary terms for culinary terms quiz
        if (quizType === 'culinary-terms') {
          const termsResponse = await fetch('/culinary-terms.json');
          if (!termsResponse.ok) {
            throw new Error('Failed to load culinary terms data');
          }
          const termsData = await termsResponse.json();
          setCulinaryTerms(termsData);
          setShuffledQuestions(shuffleArray(termsData));
        }
        
        // Load wine pairings for wine pairing quiz
        if (quizType === 'wine-pairing') {
          const wineResponse = await fetch('/wine-pairings.json');
          if (!wineResponse.ok) {
            throw new Error('Failed to load wine pairing data');
          }
          const wineData = await wineResponse.json();
          setWinePairings(wineData);
          setShuffledQuestions(shuffleArray(wineData));
        }
        
        // Load nutritional info for nutritional quiz
        if (quizType === 'nutritional-info') {
          const nutritionResponse = await fetch('/nutritional-info.json');
          if (!nutritionResponse.ok) {
            throw new Error('Failed to load nutritional data');
          }
          const nutritionData = await nutritionResponse.json();
          setNutritionalInfo(nutritionData);
          setShuffledQuestions(shuffleArray(nutritionData));
        }
        
        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        setIsLoading(false);
      }
    };

    loadData();
  }, [quizType]);

  // Handle answer selection
  const handleAnswer = (isCorrect, userAnswer, correctAnswer) => {
    const currentQuestion = shuffledQuestions[currentIndex];
    
    // Record the result
    const result = {
      question: currentQuestion,
      isCorrect,
      userAnswer,
      correctAnswer,
      questionIndex: currentIndex
    };
    
    setQuizResults(prev => [...prev, result]);
    
    if (isCorrect) {
      setScore(score + 1);
    }
    
    // Move to next question after a delay
    setTimeout(() => {
      setCurrentIndex(currentIndex + 1);
    }, 2000);
  };

  // Restart quiz
  const restartQuiz = () => {
    setCurrentIndex(0);
    setScore(0);
    setQuizResults([]);
    setShowReview(false);
    
    switch (quizType) {
      case 'culinary-terms':
        setShuffledQuestions(shuffleArray(culinaryTerms));
        break;
      case 'wine-pairing':
        setShuffledQuestions(shuffleArray(winePairings));
        break;
      case 'nutritional-info':
        setShuffledQuestions(shuffleArray(nutritionalInfo));
        break;
      default:
        const filteredItems = filterMenuItems(menuItems, quizType);
        setShuffledQuestions(shuffleArray(filteredItems));
        break;
    }
  };

  // Show review
  const handleShowReview = () => {
    setShowReview(true);
  };

  // Hide review (back to final score)
  const handleHideReview = () => {
    setShowReview(false);
  };

  // Get quiz title
  const getQuizTitle = () => {
    switch (quizType) {
      case 'description-matching':
        return 'Description Matching Quiz';
      case 'build-description':
        return 'Build the Description Quiz';
      case 'wine-pairing':
        return 'Wine Pairing Expert Quiz';
      case 'nutritional-info':
        return 'Nutritional & Dietary Quiz';
      case 'drinks-only':
        return 'Drinks Quiz';
      case 'brunch-only':
        return 'Brunch Quiz';
      case 'dinner-only':
        return 'Dinner Quiz';
      case 'culinary-terms':
        return 'Culinary Terms Quiz';
      default:
        return 'Red Tavern Quiz';
    }
  };

  // Render appropriate question card based on quiz type
  const renderQuestionCard = () => {
    const currentQuestion = shuffledQuestions[currentIndex];
    
    switch (quizType) {
      case 'build-description':
        return (
          <BuildDescriptionCard
            question={currentQuestion}
            allItems={menuItems}
            onAnswer={handleAnswer}
          />
        );
      case 'culinary-terms':
        return (
          <CulinaryTermsCard
            question={currentQuestion}
            allTerms={culinaryTerms}
            onAnswer={handleAnswer}
          />
        );
      case 'wine-pairing':
        return (
          <WinePairingCard
            question={currentQuestion}
            allPairings={winePairings}
            onAnswer={handleAnswer}
          />
        );
      case 'nutritional-info':
        return (
          <NutritionalInfoCard
            question={currentQuestion}
            allNutritionalInfo={nutritionalInfo}
            onAnswer={handleAnswer}
          />
        );
      default:
        return (
          <QuestionCard
            question={currentQuestion}
            allItems={menuItems}
            onAnswer={handleAnswer}
          />
        );
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="quiz-container">
        <div className="quiz-header">
          <button className="back-button" onClick={onBackToMenu}>
            ← Back to Menu
          </button>
          <h2>{getQuizTitle()}</h2>
        </div>
        <div className="loading">Loading quiz data...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="quiz-container">
        <div className="quiz-header">
          <button className="back-button" onClick={onBackToMenu}>
            ← Back to Menu
          </button>
          <h2>{getQuizTitle()}</h2>
        </div>
        <div className="error">Error: {error}. Please try again.</div>
      </div>
    );
  }

  // Quiz complete - show review if requested
  if (currentIndex >= shuffledQuestions.length && showReview) {
    return (
      <div className="quiz-container">
        <div className="quiz-header">
          <button className="back-button" onClick={onBackToMenu}>
            ← Back to Menu
          </button>
          <h2>{getQuizTitle()} - Review</h2>
        </div>
        <QuizReview 
          results={quizResults}
          quizType={quizType}
          onBackToScore={handleHideReview}
        />
      </div>
    );
  }

  // Quiz complete - show final score
  if (currentIndex >= shuffledQuestions.length) {
    return (
      <div className="quiz-container">
        <div className="quiz-header">
          <button className="back-button" onClick={onBackToMenu}>
            ← Back to Menu
          </button>
          <h2>{getQuizTitle()}</h2>
        </div>
        <FinalScore 
          score={score} 
          total={shuffledQuestions.length} 
          onRestart={restartQuiz}
          onShowReview={handleShowReview}
          quizType={quizType}
        />
      </div>
    );
  }

  // Quiz in progress
  return (
    <div className="quiz-container">
      <div className="quiz-header">
        <button className="back-button" onClick={onBackToMenu}>
          ← Back to Menu
        </button>
        <h2>{getQuizTitle()}</h2>
      </div>
      <div className="progress">
        {currentIndex + 1} / {shuffledQuestions.length}
      </div>
      {renderQuestionCard()}
    </div>
  );
};

export default EnhancedQuiz; 